import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Search as SearchIcon,
  FlagOutlined as FlagIcon,
  AssignmentOutlined as FormIcon,
  Link as LinkIcon,
  RemoveCircleOutline as DisconnectedIcon,
  CheckCircleOutline as ConnectedIcon,
  PeopleOutlined as PeopleIcon,
  DoneAll as DoneIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import {
  getMetaConfig,
  getMetaPagesForms,
  connectMetaLeads,
  getMetaIntegrationStatus,
  disconnectMetaLeads,
  syncMetaLeads
} from '../../services/facebook';
import { leadService } from '../../services/lead';
import ConfirmModal from '../../components/ConfirmModal';

const Facebook = () => {
  const theme = useTheme();

  // Permanent and temporary connection states
  const [connectedIntegration, setConnectedIntegration] = useState(null);
  const [authorizedSession, setAuthorizedSession] = useState(null);

  // Configuration flow states
  const [loginUrl, setLoginUrl] = useState('');
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  
  const [crmDefaults] = useState({
    defaultAssociateId: '',
    defaultInterestedIn: '',
    defaultStatus: 'New',
    defaultStage: 'Lead',
    sourceLabel: 'Facebook Lead Ads',
    defaultAddress: {
      addressLine: '',
      city: '',
      state: ''
    },
    notes: ''
  });

  // UI States
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [searchPageQuery, setSearchPageQuery] = useState('');
  const [searchFormQuery, setSearchFormQuery] = useState('');

  // Sync manual states
  const [selectedSyncFormId, setSelectedSyncFormId] = useState('all');
  const [maxSyncItems, setMaxSyncItems] = useState(100);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [syncedLeads, setSyncedLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leadsPage, setLeadsPage] = useState(0);
  const [leadsRowsPerPage, setLeadsRowsPerPage] = useState(10);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);

  const getLeadsCount = () => {
    if (!connectedIntegration) return '0';
    
    // Prioritize exact known keys first
    const primaryKeys = ['leadsReceived', 'totalLeadsReceived', 'totalDocs', 'leadsCount'];
    for (const key of primaryKeys) {
      const val = connectedIntegration[key];
      if (val !== undefined && val !== null && !isNaN(val)) {
        return Number(val);
      }
    }

    // Dynamic search: find any numeric key containing "lead", "doc", or "received"
    for (const key of Object.keys(connectedIntegration)) {
      const lowerKey = key.toLowerCase();
      if (
        (lowerKey.includes('lead') || lowerKey.includes('doc') || lowerKey.includes('received')) &&
        !lowerKey.includes('form') &&
        !lowerKey.includes('associate') &&
        !lowerKey.includes('interested')
      ) {
        const val = connectedIntegration[key];
        if (val !== undefined && val !== null && !isNaN(val) && typeof val !== 'boolean') {
          return Number(val);
        }
      }
    }

    // Fallback to locally loaded leads list length
    if (syncedLeads && syncedLeads.length > 0) {
      return syncedLeads.length;
    }

    return '0';
  };

  const fetchSyncedLeads = async (customPage = leadsPage, customLimit = leadsRowsPerPage) => {
    // Safely extract numeric values in case customPage/customLimit receive React events or objects
    let pageNum = 0;
    if (typeof customPage === 'number') {
      pageNum = customPage;
    } else if (typeof customPage === 'string' && !isNaN(customPage)) {
      pageNum = parseInt(customPage, 10);
    } else if (typeof leadsPage === 'number') {
      pageNum = leadsPage;
    } else if (typeof leadsPage === 'string' && !isNaN(leadsPage)) {
      pageNum = parseInt(leadsPage, 10);
    }

    let limitNum = 10;
    if (typeof customLimit === 'number') {
      limitNum = customLimit;
    } else if (typeof customLimit === 'string' && !isNaN(customLimit)) {
      limitNum = parseInt(customLimit, 10);
    } else if (typeof leadsRowsPerPage === 'number') {
      limitNum = leadsRowsPerPage;
    } else if (typeof leadsRowsPerPage === 'string' && !isNaN(leadsRowsPerPage)) {
      limitNum = parseInt(leadsRowsPerPage, 10);
    }

    setLeadsLoading(true);
    try {
      console.log('[Meta Integration] Fetching Facebook Lead Ads leads with page:', pageNum + 1, 'limit:', limitNum);
      const leadsData = await leadService.getLeads({ 
        provider: 'meta', 
        page: pageNum + 1, 
        limit: limitNum 
      });
      console.log('[Meta Integration] Fetched Central Meta leads success. Data:', leadsData);
      
      let leadsList = [];
      if (leadsData && Array.isArray(leadsData.docs)) {
        leadsList = leadsData.docs;
      } else if (Array.isArray(leadsData)) {
        leadsList = leadsData;
      } else if (leadsData && Array.isArray(leadsData.leads)) {
        leadsList = leadsData.leads;
      } else if (leadsData && Array.isArray(leadsData.data)) {
        leadsList = leadsData.data;
      }
      setSyncedLeads(leadsList);
      setTotalLeadsCount(leadsData?.totalDocs || leadsList.length || 0);
    } catch (err) {
      console.error('[Meta Integration Error] Failed to fetch leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleChangeLeadsPage = (event, newPage) => {
    const pageVal = typeof newPage === 'number' ? newPage : (typeof event === 'number' ? event : 0);
    setLeadsPage(pageVal);
  };

  const handleChangeLeadsRowsPerPage = (event) => {
    const newLimit = parseInt(event?.target?.value, 10);
    if (!isNaN(newLimit)) {
      setLeadsRowsPerPage(newLimit);
      setLeadsPage(0);
    }
  };

  // Automatically fetch leads when page or rowsPerPage changes
  useEffect(() => {
    if (connectedIntegration) {
      fetchSyncedLeads(leadsPage, leadsRowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsPage, leadsRowsPerPage]);

  // 1. Fetch backend configurations and source-of-truth integration status
  const fetchMetaIntegrations = async () => {
    console.log('[Meta Integration] fetchMetaIntegrations initialized.');
    console.log('[Meta Integration] Target API Base URL:', process.env.REACT_APP_API_BASE_URL);
    setStatusLoading(true);
    try {
      // 1. Fetch Config
      console.log('[Meta Integration] Calling GET /api/meta-leads/config ...');
      const configRes = await getMetaConfig();
      console.log('[Meta Integration] GET /api/meta-leads/config success. Response:', configRes);
      
      if (configRes?.status && configRes?.data?.loginUrl) {
        setLoginUrl(configRes.data.loginUrl);
      } else if (configRes?.loginUrl) {
        setLoginUrl(configRes.loginUrl);
      }

      // 2. Fetch active integration status from backend database (Absolute Source of Truth)
      console.log('[Meta Integration] Calling GET /api/meta-leads ...');
      const statusRes = await getMetaIntegrationStatus();
      console.log('[Meta Integration] GET /api/meta-leads success. Response:', statusRes);

      // Deep recursive search to find any object where status === 'connected' and isActive === true
      const findConnectedItem = (obj) => {
        if (!obj) return null;
        if (Array.isArray(obj)) {
          for (let item of obj) {
            const found = findConnectedItem(item);
            if (found) return found;
          }
        } else if (typeof obj === 'object') {
          if (obj.status === 'connected' && obj.isActive === true) {
            return obj;
          }
          for (let key in obj) {
            if (obj[key] && typeof obj[key] === 'object') {
              const found = findConnectedItem(obj[key]);
              if (found) return found;
            }
          }
        }
        return null;
      };

      const connected = findConnectedItem(statusRes);
      console.log('[Meta Integration] Resolved connectedIntegration object:', connected);
      setConnectedIntegration(connected);
      if (connected) {
        fetchSyncedLeads();
      }
    } catch (err) {
      console.error('[Meta Integration Error] fetchMetaIntegrations failed!');
      console.error('[Meta Integration Error] Details:', err);
      setError(err.message || 'Failed to check active integration status.');
    } finally {
      setStatusLoading(false);
      console.log('[Meta Integration] fetchMetaIntegrations completed.');
    }
  };

  const handleSyncLeads = async () => {
    if (syncLoading) return; // Prevent double click
    if (!connectedIntegration?.pageId) {
      setError('No active connected integration found.');
      return;
    }

    setSyncLoading(true);
    setError(null);
    setSuccess(null);
    setSyncSummary(null);

    try {
      const payload = {
        pageId: connectedIntegration.pageId,
        maxItems: maxSyncItems
      };

      if (selectedSyncFormId !== 'all') {
        payload.formId = selectedSyncFormId;
      }

      console.log('[Meta Integration] Triggering manual sync with payload:', payload);
      const res = await syncMetaLeads(payload);
      console.log('[Meta Integration] Sync complete. Response:', res);

      const summary = res?.summary || res?.data?.summary || res?.data;
      if (summary) {
        setSyncSummary(summary);
      }

      // Success toast with "X leads synced"
      const processedCount = summary?.processed || 0;
      setSuccess(`${processedCount} leads synced`);

      // Refresh integration/status cards
      await fetchMetaIntegrations();
      await fetchSyncedLeads();
    } catch (err) {
      console.error('[Meta Integration Error] Sync failed:', err);
      setError(err.message || 'Leads sync failed.');
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    fetchMetaIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Event listener for Facebook popup window oauth callbacks
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      const messageData = event.data;
      
      console.log('[Window Message Event Received] Origin:', event.origin, 'Data:', messageData);

      if (messageData?.type === 'META_OAUTH_SUCCESS') {
        const payload = messageData.payload || messageData;
        if (payload?.sessionId) {
          // Store temporary OAuth preview session
          setAuthorizedSession(payload);
          setPages(payload.pages || []);
          setSuccess('Facebook authorization completed successfully! Select your page and forms to finalize connection.');
          setError(null);
        }
      } else if (messageData?.type === 'META_OAUTH_ERROR') {
        const errMsg = messageData.payload?.message || messageData.message || 'Facebook authorization failed.';
        setError(`Facebook authorization failed: ${errMsg}`);
        setSuccess(null);
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => {
      window.removeEventListener('message', handleOAuthMessage);
    };
  }, []);

  // 3. Trigger Facebook OAuth Login popup flow
  const handleContinueWithFacebook = () => {
    console.log('[Meta Integration] Continuing with Facebook OAuth. loginUrl:', loginUrl);
    if (!loginUrl) {
      setError('Facebook login URL not configured. Please contact administrator.');
      return;
    }

    try {
      const u = new URL(loginUrl);
      console.log('[Meta OAuth Params Debug]:');
      console.log('client_id:', u.searchParams.get('client_id'));
      console.log('redirect_uri:', u.searchParams.get('redirect_uri'));
      console.log('response_type:', u.searchParams.get('response_type'));
      console.log('scope:', u.searchParams.get('scope'));
    } catch (err) {
      console.error('[Meta Integration] Could not parse loginUrl as a standard URL object. Error:', err);
    }

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      loginUrl,
      'Meta Lead Ads Authorization',
      `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
    );
  };

  // 4. Page Selected in preview workflow: fetch page forms
  const handleSelectPage = async (page) => {
    console.log('[Meta Integration] Page Selected:', page);
    console.log('[Meta Integration] Fetching forms for pageId:', page.id, 'sessionId:', authorizedSession?.sessionId);
    setSelectedPage(page);
    setForms([]);
    setSelectedForms([]);
    setLoading(true);
    setError(null);
    try {
      const res = await getMetaPagesForms(page.id, authorizedSession?.sessionId);
      console.log('[Meta Integration] GET /api/meta-leads/pages/.../forms response:', res);
      
      // Fallback handlers to extract forms array under various backend payload structures
      let fetchedForms = [];
      if (res?.data?.forms && Array.isArray(res.data.forms)) {
        fetchedForms = res.data.forms;
      } else if (res?.forms && Array.isArray(res.forms)) {
        fetchedForms = res.forms;
      } else if (res?.status && Array.isArray(res?.data)) {
        fetchedForms = res.data;
      } else if (Array.isArray(res)) {
        fetchedForms = res;
      } else if (res?.data && Array.isArray(res.data.data)) {
        fetchedForms = res.data.data;
      } else if (res?.data && Array.isArray(res.data)) {
        fetchedForms = res.data;
      }
      
      console.log('[Meta Integration] Parsed fetchedForms list:', fetchedForms);
      setForms(fetchedForms);
    } catch (err) {
      console.error('[Meta Integration Error] Failed to fetch Lead Forms for this page:', err);
      setError(err.message || 'Failed to fetch Lead Forms for this page.');
    } finally {
      setLoading(false);
    }
  };

  // 5. Toggle multi-select checkbox status
  const handleToggleForm = (formId) => {
    setSelectedForms(prev => 
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId]
    );
  };

  // 6. Connect page & active forms integration to database (Permanent Connect)
  const handleConnectIntegration = async () => {
    if (!selectedPage) {
      setError('Please select a Facebook page first.');
      return;
    }
    if (selectedForms.length === 0) {
      setError('Please select at least one lead form.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        sessionId: authorizedSession?.sessionId,
        pageId: selectedPage.id,
        selectedForms,
        defaultAssociateId: crmDefaults.defaultAssociateId,
        defaultInterestedIn: crmDefaults.defaultInterestedIn,
        defaultStatus: crmDefaults.defaultStatus,
        defaultStage: crmDefaults.defaultStage,
        sourceLabel: crmDefaults.sourceLabel,
        defaultAddress: crmDefaults.defaultAddress,
        notes: crmDefaults.notes
      };

      const res = await connectMetaLeads(payload);
      if (res) {
        setSuccess('Integration connected successfully! Real-time leads are now streaming.');
        // Reset temporary authorized session state
        setAuthorizedSession(null);
        setSelectedPage(null);
        setPages([]);
        setForms([]);
        setSelectedForms([]);
        // Sync & refresh status of permanent connection with backend (Source of Truth)
        await fetchMetaIntegrations();
      }
    } catch (err) {
      setError(err.message || 'Failed to connect integration.');
    } finally {
      setLoading(false);
    }
  };

  // 7. Disconnect active permanent integration from database
  const handleDisconnect = () => {
    setConfirmOpen(true);
  };

  const executeDisconnect = async () => {
    setConfirmOpen(false);
    const targetId = connectedIntegration?._id || connectedIntegration?.id;
    if (!targetId) return;

    setLoading(true);
    setError(null);
    try {
      await disconnectMetaLeads(targetId);
      setSuccess('Integration disconnected successfully.');
      setConnectedIntegration(null);
      setAuthorizedSession(null);
      await fetchMetaIntegrations();
    } catch (err) {
      setError(err.message || 'Failed to disconnect integration.');
    } finally {
      setLoading(false);
    }
  };

  // List filters
  const filteredPages = pages.filter(p => 
    p.name.toLowerCase().includes(searchPageQuery.toLowerCase())
  );

  const filteredForms = forms.filter(f => 
    f.name.toLowerCase().includes(searchFormQuery.toLowerCase())
  );

  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking integration status...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FacebookIcon sx={{ color: '#1877F2', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
            Meta Lead Ads Integration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => {
            fetchMetaIntegrations();
            fetchSyncedLeads();
          }}
          disabled={statusLoading || leadsLoading}
          startIcon={<SyncIcon />}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: 'white',
            borderColor: '#e2e8f0',
            color: '#475569',
            '&:hover': {
              bgcolor: '#f8fafc',
              borderColor: '#cbd5e1'
            }
          }}
        >
          Refresh Feed
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Connect / Connected Status Top Card */}
      <Card sx={{ borderRadius: 4, mb: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
        <CardContent sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
              {connectedIntegration ? 'Facebook Account Connected' : 'Connect Your Facebook Account'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {connectedIntegration 
                ? `Your Facebook page "${connectedIntegration.pageName || 'Connected Page'}" is authorized. Real-time leads sync is active.` 
                : 'Authorize your Facebook account to access pages, forms and leads.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {connectedIntegration ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={loading}
                sx={{
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Disconnect
              </Button>
            ) : authorizedSession ? (
              <Chip label="Authorized" color="success" sx={{ fontWeight: 800, py: 2.5, px: 2, borderRadius: 3 }} />
            ) : (
              <Button
                variant="contained"
                startIcon={<FacebookIcon />}
                onClick={handleContinueWithFacebook}
                sx={{
                  borderRadius: 2.5,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  backgroundColor: '#1877F2',
                  boxShadow: '0 8px 16px ' + alpha('#1877F2', 0.2),
                  '&:hover': {
                    backgroundColor: '#166fe5',
                    boxShadow: '0 8px 20px ' + alpha('#1877F2', 0.3)
                  }
                }}
              >
                Continue with Facebook
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Temporary Oauth Authorization Preview Flow */}
      {!connectedIntegration && authorizedSession && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Pages Column */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                  <FlagIcon sx={{ color: '#1877F2', fontSize: 24, mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Pages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a page to view lead forms and manage leads.
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search for page..."
                  value={searchPageQuery}
                  onChange={(e) => setSearchPageQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: '#ffffff',
                    }
                  }}
                />

                {filteredPages.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredPages.map((page) => {
                      const isSelected = selectedPage?.id === page.id;
                      return (
                        <Paper
                          key={page.id}
                          variant="outlined"
                          onClick={() => handleSelectPage(page)}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderColor: isSelected ? 'primary.main' : 'grey.200',
                            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                            '&:hover': {
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.100', color: isSelected ? 'white' : 'text.secondary', width: 40, height: 40 }}>
                              <FlagIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={800}>
                                {page.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {page.id}
                              </Typography>
                            </Box>
                          </Box>
                          {isSelected && <Chip label="Selected" color="primary" size="small" sx={{ fontWeight: 700 }} />}
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                    <FlagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                      No pages found.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Campaigning (Lead Forms) Column */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                  <FormIcon sx={{ color: '#1877F2', fontSize: 24, mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Campaigning (Lead Forms)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a page to view available lead forms.
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search for forms..."
                  value={searchFormQuery}
                  onChange={(e) => setSearchFormQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: '#ffffff',
                    }
                  }}
                />

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : filteredForms.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredForms.map((form) => {
                      const isChecked = selectedForms.includes(form.id);
                      return (
                        <Paper
                          key={form.id}
                          variant="outlined"
                          onClick={() => handleToggleForm(form.id)}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            borderColor: isChecked ? 'primary.main' : 'grey.200',
                            backgroundColor: isChecked ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                            '&:hover': {
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                          <Checkbox checked={isChecked} color="primary" />
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="body2" fontWeight={800}>
                              {form.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {form.id}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                    <FormIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                      {selectedPage ? 'No forms found for this page.' : 'Select a page to view available lead forms.'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* CRM Lead Defaults Form (Commented out mapping form fields but kept the Connect action button) */}
      {!connectedIntegration && authorizedSession && selectedPage && selectedForms.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<DoneIcon />}
            disabled={loading}
            onClick={handleConnectIntegration}
            sx={{
              borderRadius: 2.5,
              px: 4,
              py: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: 'primary.main',
              boxShadow: '0 4px 12px ' + alpha(theme.palette.primary.main, 0.15),
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: '0 6px 16px ' + alpha(theme.palette.primary.main, 0.25)
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Finalize & Connect Page'}
          </Button>
        </Box>
      )}

      {/* Integration Status Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LinkIcon sx={{ color: '#1877F2', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Integration Status
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Connection Status Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: connectedIntegration ? alpha(theme.palette.success.main, 0.1) : 'grey.100', color: connectedIntegration ? 'success.main' : 'text.disabled', width: 44, height: 44 }}>
                  {connectedIntegration ? <ConnectedIcon /> : <DisconnectedIcon />}
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                    Connection Status
                  </Typography>
                  <Chip 
                    label={connectedIntegration ? 'Connected' : 'Not Connected'} 
                    size="small" 
                    sx={{ 
                      fontWeight: 800, 
                      fontSize: '0.75rem',
                      bgcolor: connectedIntegration ? alpha(theme.palette.success.main, 0.1) : 'grey.100',
                      color: connectedIntegration ? 'success.main' : 'text.disabled'
                    }} 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Pages Count Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha('#1877F2', 0.1), color: '#1877F2', width: 44, height: 44 }}>
                  <FlagIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    Pages
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="text.primary">
                    {connectedIntegration ? '1' : '0'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Lead Forms Count Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', width: 44, height: 44 }}>
                  <FormIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    Lead Forms
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="text.primary">
                    {connectedIntegration ? (connectedIntegration.selectedForms?.length || '0') : '0'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Last Sync Card */}
          {/* <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', width: 44, height: 44 }}>
                  <ClockIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    Last Sync
                  </Typography>
                  <Typography variant="body1" fontWeight={800} color="text.primary">
                    {connectedIntegration ? 'Just now' : 'Never'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid> */}

          {/* Leads Received Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none' }}>
              <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', width: 44, height: 44 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    Leads Received
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="text.primary">
                    {getLeadsCount()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Note Alert banner */}
        {/* <Paper 
          variant="outlined" 
          sx={{ 
           
          }}
        >
          <InfoIcon sx={{ color: '#1877F2', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            <strong>Note:</strong> We never post on your behalf. This integration is read-only for leads.
          </Typography>
        </Paper> */}
      </Box>

      {/* Manual Sync and Synced Leads List (Visible only when connected) */}
      {connectedIntegration && (
        <>
          {/* Sync Card */}
          <Card sx={{ borderRadius: 4, mb: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <SyncIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Sync Leads Manually
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manually trigger synchronization of leads from your Facebook Lead Ads forms.
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3} alignItems="flex-end">
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth>
                    <InputLabel id="sync-form-label">Select Form to Sync</InputLabel>
                    <Select
                      labelId="sync-form-label"
                      id="sync-form-select"
                      value={selectedSyncFormId}
                      label="Select Form to Sync"
                      onChange={(e) => setSelectedSyncFormId(e.target.value)}
                      sx={{ borderRadius: 3 }}
                    >
                      <MenuItem value="all"><em>All Connected Forms</em></MenuItem>
                      {connectedIntegration?.selectedForms?.map((form) => {
                        const id = typeof form === 'object' ? form.formId : form;
                        const label = typeof form === 'object' ? (form.name || `Form ID: ${form.formId}`) : `Form ID: ${form}`;
                        return (
                          <MenuItem key={id} value={id}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Items to Sync"
                    value={maxSyncItems}
                    onChange={(e) => setMaxSyncItems(Math.max(1, parseInt(e.target.value) || 100))}
                    InputProps={{ inputProps: { min: 1, max: 1000 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={syncLoading}
                    onClick={handleSyncLeads}
                    startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                    sx={{
                      py: 1.8,
                      borderRadius: 3,
                      fontWeight: 700,
                      textTransform: 'none',
                      backgroundColor: 'primary.main',
                      boxShadow: '0 4px 12px ' + alpha(theme.palette.primary.main, 0.15),
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        boxShadow: '0 6px 16px ' + alpha(theme.palette.primary.main, 0.25)
                      }
                    }}
                  >
                    {syncLoading ? 'Syncing...' : 'Sync Leads Now'}
                  </Button>
                </Grid>
              </Grid>

              {/* Sync Summary Section */}
              {syncSummary && (
                <Box sx={{ mt: 4, p: 3, backgroundColor: '#f8fafc', borderRadius: 3, border: '1px solid', borderColor: 'grey.100' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoneIcon sx={{ color: 'success.main' }} /> Sync Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'grey.100', textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={800} color="success.main">
                          {syncSummary.processed || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          CRM Leads Created
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'grey.100', textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={800} color="info.main">
                          {syncSummary.duplicate || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Already Saved
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'grey.100', textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={800} color="warning.main">
                          {syncSummary.pending_details || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Phone/Details Issue
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'grey.100', textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={800} color="error.main">
                          {syncSummary.failed || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Sync Errors
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Synced Leads Table */}
          <Card sx={{ borderRadius: 4, mb: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Recent Synced Leads
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Leads automatically or manually synchronized from Facebook Lead Ads.
                  </Typography>
                </Box>
                <Button 
                  size="small" 
                  startIcon={<SyncIcon />} 
                  onClick={() => fetchSyncedLeads()}
                  disabled={leadsLoading}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Refresh List
                </Button>
              </Box>

              {leadsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : syncedLeads.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Phone / Email</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Date Synced</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Assigned Associate</TableCell>
                          {/* <TableCell sx={{ fontWeight: 700 }}>Assignment Audit</TableCell> */}
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {syncedLeads.map((lead) => {
                          const statusColors = (lead.status || '').toLowerCase() === 'new' 
                            ? { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }
                            : (lead.status || '').toLowerCase() === 'contacted' || (lead.status || '').toLowerCase() === 'followup'
                            ? { bg: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }
                            : { bg: 'grey.100', color: 'text.secondary' };
                          
                          return (
                            <TableRow key={lead._id || lead.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {lead.customerDetails?.name || lead.name || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {lead.customerDetails?.phone || lead.phone || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {lead.customerDetails?.email || lead.email || ''}
                                </Typography>
                              </TableCell>
                              <TableCell>{lead.source || 'Facebook'}</TableCell>
                              <TableCell>
                                {lead.createdAt 
                                  ? new Date(lead.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) 
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {lead.associateId ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                      {lead.associateDetails?.name || 'Unknown'} (ID: {lead.associateDetails?.associateId || '—'})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {lead.associateDetails?.designation || '—'} | {lead.associateDetails?.phone || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {lead.associateDetails?.email || '—'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Chip label="Unassigned" size="small" variant="outlined" color="warning" />
                                )}
                              </TableCell>
                              {/* <TableCell>
                                {lead.assignmentInfo ? (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Strategy: {lead.assignmentInfo.routingStrategy || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      By: {lead.assignmentInfo.assignedBy || 'System'} | Source: {lead.assignmentInfo.source || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" display="block">
                                      {lead.assignmentInfo.assignedAt ? new Date(lead.assignmentInfo.assignedAt).toLocaleString() : '—'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  '—'
                                )}
                              </TableCell> */}
                              <TableCell>
                                <Chip 
                                  label={lead.status || 'New'} 
                                  size="small" 
                                  sx={{ 
                                    fontWeight: 800, 
                                    bgcolor: statusColors.bg, 
                                    color: statusColors.color 
                                  }} 
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={totalLeadsCount}
                    rowsPerPage={leadsRowsPerPage}
                    page={leadsPage}
                    onPageChange={handleChangeLeadsPage}
                    onRowsPerPageChange={handleChangeLeadsRowsPerPage}
                    sx={{
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'text.secondary'
                      },
                      '& .MuiTablePagination-select': {
                        fontSize: '13px',
                        fontWeight: 600
                      }
                    }}
                  />
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                    No synced leads found.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Use the manual sync tool above or wait for incoming webhooks.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Pages & Campaigning Initial State UI (Only when NOT connected and NO oauth session) */}
      {!connectedIntegration && !authorizedSession && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                  <FlagIcon sx={{ color: '#1877F2', fontSize: 24, mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Pages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a page to view lead forms and manage leads.
                    </Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  disabled
                  placeholder="Search for page..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 4 }}
                />
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                  <FlagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                    No pages found.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Connect your Facebook account to see your pages.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                  <FormIcon sx={{ color: '#1877F2', fontSize: 24, mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Campaigning (Lead Forms)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a page to view lead forms.
                    </Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  disabled
                  placeholder="Search for forms..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 4 }}
                />
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                  <FormIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                    No forms found.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Select a page to view available lead forms.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Disconnect confirmation modal */}
      <ConfirmModal
        open={confirmOpen}
        title="Disconnect Integration?"
        type="warning"
        confirmText="Disconnect"
        cancelText="Cancel"
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeDisconnect}
      >
        Are you sure you want to disconnect your Meta Lead Ads integration? Synced leads will remain, but real-time streaming will stop immediately.
      </ConfirmModal>
    </Box>
  );
};

export default Facebook;
